import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import I18nProvider from './i18n/I18nProvider';
import Home from './components/Home';
import About from './components/About';
import FAQ from './components/FAQ';
import HowToPlay from './components/HowToPlay';
import Blog from './components/Blog';
import Privacy from './components/Privacy';
import TvScreen from './components/TvScreen';
import SeoPage from './components/SeoPage';
import { LANDINGS, PAGES } from './seoPages';
import { ensureAuth } from './utils/auth';
import { ensureUserProfile } from './utils/userProfile';
import { ensureIdentified } from './utils/posthogAnalytics';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function Boot() {
  const { pathname } = useLocation();
  const isTv = pathname.startsWith('/tv/');

  useEffect(() => {
    // The table screen is read-only. Skip auth, /users writes, identify, and
    // page-visit counters so opening /tv/CODE never writes to the RTDB.
    if (isTv) return undefined;

    const runWhenIdle = (callback) => {
      if ('requestIdleCallback' in window) {
        const idleId = window.requestIdleCallback(callback, { timeout: 3000 });
        return () => window.cancelIdleCallback(idleId);
      }

      const timeoutId = window.setTimeout(callback, 1000);
      return () => window.clearTimeout(timeoutId);
    };

    let cancelPageVisitTracking = null;
    let isMounted = true;

    // Initialize auth before any Firebase operations
    const initAuth = async () => {
      try {
        await ensureAuth();
        // Tie PostHog events to the same stable anonymous identity used
        // everywhere else. Shared helper (cached promise) so mount-time
        // captures can await the same identify instead of racing it.
        // No-op when PostHog wasn't initialized (no key).
        await ensureIdentified();
        console.log('🔐 Authentication initialized');

        // Touch the persistent profile (creates it on first visit, refreshes
        // lastSeen on return). Best-effort — failure never blocks the app.
        await ensureUserProfile();

        if (!isMounted) {
          return;
        }

        // Track page visits after the home screen has had a chance to read critical stats.
        cancelPageVisitTracking = runWhenIdle(async () => {
          const { trackPageVisit } = await import('./utils/analytics');
          trackPageVisit();
        });
      } catch (error) {
        console.error('🔐 Auth initialization failed:', error);
      }
    };

    initAuth();

    return () => {
      isMounted = false;
      if (cancelPageVisitTracking) {
        cancelPageVisitTracking();
      }
    };
  }, [isTv]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
      <Router>
        <ScrollToTop />
        <Boot />
        <div className="min-h-screen flex flex-col">
          {/* Main App Content */}
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/join/:code" element={<Home />} />
              <Route path="/r/:code/:token" element={<Home />} />
              <Route path="/tv/:code" element={<TvScreen />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/how-to-play-poker-without-chips" element={<HowToPlay />} />
              <Route path="/blog" element={<Blog />} />
              {LANDINGS.map((l) => (
                <Route key={l.id} path={l.path} element={<SeoPage page={PAGES[l.id]} />} />
              ))}
              <Route path="/privacy" element={<Privacy />} />
            </Routes>
          </main>
        </div>
      </Router>
      </I18nProvider>
    </HelmetProvider>
  );
}

export default App;
